import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  useTelemedicineSession,
  useJoinSession,
  useLeaveSession,
  useEndSession,
  useSessionParticipants,
  useGenerateToken,
  useRealtimeEvents,
  useRecording,
} from '@/hooks/telemedicine';
import {
  VideoStage,
  MediaControls,
  ParticipantsList,
  SessionChat,
  ChatMessage,
  TelemedicinePrescriptionModal,
} from '@/components/telemedicine';
import { Button } from '@/components/ui/Button';
import { telemedicineApi, expressApi, medicalRecordsApi, doctorsApi, type Prescription } from '@/api';
import { MessageSquare, FileText, Sparkles, Pill, Save, Check, ExternalLink, Lock, Key, Copy, ShieldCheck, RefreshCw, X } from 'lucide-react';
import { useDoctorProfile } from '@/hooks/doctor/useDoctorProfile';

export function VideoCallPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const { data: profile } = useDoctorProfile(user?.id || '');

  const [cameraOn, setCameraOn] = useState(true);
  const [microphoneOn, setMicrophoneOn] = useState(true);
  const [screenShareOn, setScreenShareOn] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [connectionState, setConnectionState] = useState<'CONNECTING' | 'CONNECTED' | 'RECONNECTING' | 'DISCONNECTED'>('CONNECTING');
  const [localParticipantId, setLocalParticipantId] = useState<string | null>(null);

  // Token da Daily.co e trava para execução única do join
  const [dailyToken, setDailyToken] = useState<string | null>(null);
  const hasJoinedRef = useRef(false);

  // Mobile Drawer State
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Doctor Clinical Tools State
  const [activeTab, setActiveTab] = useState<'chat' | 'triage' | 'soap' | 'prescriptions'>('chat');
  const [triageSummary, setTriageSummary] = useState<any>(null);

  const parsedTriage = useMemo(() => {
    if (!triageSummary) return null;
    if (typeof triageSummary === 'object' && triageSummary !== null) return triageSummary;
    if (typeof triageSummary === 'string') {
      try {
        const parsed = JSON.parse(triageSummary);
        if (typeof parsed === 'object' && parsed !== null) return parsed;
      } catch {
        return { summary: triageSummary, chiefComplaint: triageSummary };
      }
    }
    return null;
  }, [triageSummary]);

  const [soapSubjective, setSoapSubjective] = useState('');
  const [soapObjective, setSoapObjective] = useState('');
  const [soapAssessment, setSoapAssessment] = useState('');
  const [soapPlan, setSoapPlan] = useState('');
  const [soapSaved, setSoapSaved] = useState(false);

  const { data: session, refetch: refetchSession } = useTelemedicineSession(sessionId);
  const { data: participants = [] } = useSessionParticipants(sessionId);
  const { events } = useRealtimeEvents({ sessionId });
  const { isRecording, startRecording, stopRecording, isStarting, isStopping } = useRecording(sessionId || '');

  const joinSession = useJoinSession();
  const leaveSession = useLeaveSession();
  const endSession = useEndSession();
  const generateToken = useGenerateToken();

  // Polling rápido enquanto roomId não estiver populado pelo backend
  useEffect(() => {
    if (session && !session.roomId) {
      console.info('[VIDEOCALL-PAGE] Aguardando criação/provisionamento da sala Daily.co pelo backend...');
      const interval = setInterval(() => {
        refetchSession();
      }, 1000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [session?.roomId, refetchSession]);

  // Load triage data for express sessions
  useEffect(() => {
    if (session?.appointmentId) {
      expressApi.getStatusByAppointment(session.appointmentId)
        .then((res: any) => {
          if (res?.summaryAI) {
            setTriageSummary(res.summaryAI);
          }
        })
        .catch(() => {});
    }
  }, [session?.appointmentId]);

  const handleRecordingToggle = async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  };

  const handleMediaToggle = useCallback(async (type: 'camera' | 'microphone' | 'screenShare', value: boolean) => {
    if (!sessionId || !localParticipantId) return;

    try {
      await telemedicineApi.emitMediaState({
        sessionId,
        participantId: localParticipantId,
        camera: type === 'camera' ? value : cameraOn,
        microphone: type === 'microphone' ? value : microphoneOn,
        screenShare: type === 'screenShare' ? value : screenShareOn,
      });

      if (type === 'camera') setCameraOn(value);
      if (type === 'microphone') setMicrophoneOn(value);
      if (type === 'screenShare') setScreenShareOn(value);
    } catch (err) {
      console.error('[VideoCallPage] Failed to emit media state:', err);
    }
  }, [sessionId, localParticipantId, cameraOn, microphoneOn, screenShareOn]);

  const handleCameraToggle = useCallback((value: boolean) => {
    handleMediaToggle('camera', value);
  }, [handleMediaToggle]);

  const handleMicrophoneToggle = useCallback((value: boolean) => {
    handleMediaToggle('microphone', value);
  }, [handleMediaToggle]);

  const handleScreenShareToggle = useCallback((value: boolean) => {
    handleMediaToggle('screenShare', value);
  }, [handleMediaToggle]);

  useEffect(() => {
    if (!user?.id || !session?.id || !sessionId) return;
    if (hasJoinedRef.current) return;
    hasJoinedRef.current = true;

    console.info('[VIDEOCALL-PAGE] Inicializando sessão de vídeo:', {
      sessionId,
      sessionStatus: session.status,
      sessionDoctorId: session.doctorId,
      sessionPatientId: session.patientId,
      roomUrl: session.roomId,
      userId: user.id,
    });

    const isDoctorUser = Boolean(
      user.id === session.doctorId ||
      profile?.id === session.doctorId ||
      profile?.userId === session.doctorId ||
      user.role === 'DOCTOR'
    );
    const role = isDoctorUser ? 'DOCTOR' : 'PATIENT';
    const targetSessionId = session.id;

    console.info('[VIDEOCALL-PAGE] Disparando joinSession e generateToken...', {
      targetSessionId,
      userId: user.id,
      role,
    });

    joinSession.mutateAsync({
      sessionId: targetSessionId,
      userId: user.id,
      role,
    }).then((data) => {
      console.info('[VIDEOCALL-PAGE] ✓ joinSession bem-sucedido:', data);
      setConnectionState('CONNECTED');
      if (data?.id) {
        setLocalParticipantId(data.id);
      }
      return generateToken.mutateAsync({ sessionId: targetSessionId, userId: user.id, role });
    }).then((tokenRes) => {
      console.info('[VIDEOCALL-PAGE] ✓ generateToken bem-sucedido:', tokenRes);
      if (tokenRes?.token) {
        setDailyToken(tokenRes.token);
      }
      if (!session.roomId) {
        refetchSession();
      }
    }).catch((err) => {
      console.error('[VIDEOCALL-PAGE] ✕ Erro ao conectar na sessão de vídeo:', err);
      hasJoinedRef.current = false;
      // Se a sala Daily já possui URL externa, mantém CONNECTED para liberar interface
      if (session?.roomId?.startsWith('http')) {
        setConnectionState('CONNECTED');
      } else {
        setConnectionState('DISCONNECTED');
      }
    });
  }, [session?.id, session?.doctorId, session?.roomId, user?.id, user?.role, profile?.id, profile?.userId, sessionId, refetchSession]);

  const authenticatedRoomUrl = useMemo(() => {
    if (!session?.roomId) return undefined;
    if (session.roomId.startsWith('http://') || session.roomId.startsWith('https://')) {
      if (dailyToken && !session.roomId.includes('?t=')) {
        return `${session.roomId}?t=${dailyToken}`;
      }
    }
    return session.roomId;
  }, [session?.roomId, dailyToken]);

  useEffect(() => {
    const connectionEvents = events.filter(e => e.type === 'CONNECTION_STATE');
    const latest = connectionEvents[connectionEvents.length - 1];
    if (latest && typeof latest.state === 'string') {
      setConnectionState(latest.state as typeof connectionState);
    }

    const newChat = events.filter(e => e.type === 'CHAT_MESSAGE');
    if (newChat.length > 0) {
      setChatMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const toAdd: ChatMessage[] = [];

        for (const e of newChat) {
          const rawId = String(e.messageId || (e as any).id || '');
          if (rawId && existingIds.has(rawId)) {
            continue;
          }

          let content = String(e.content || '');
          let attachment: any = undefined;

          if (content.includes('[MED_ATTACHMENT]:')) {
            const parts = content.split('[MED_ATTACHMENT]:');
            content = parts[0]?.trim() || '';
            try {
              attachment = JSON.parse(parts[1] || '{}');
            } catch {
              // fallback silencioso
            }
          }

          const id = rawId || crypto.randomUUID();
          existingIds.add(id);
          toAdd.push({
            id,
            participantId: String(e.participantId || ''),
            content,
            timestamp: String(e.timestamp),
            attachment,
          });
        }

        return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
      });
    }
  }, [events]);

  const handleLeave = async () => {
    const targetSessionId = session?.id || sessionId;
    if (user?.id && targetSessionId) {
      try {
        await leaveSession.mutateAsync({ sessionId: targetSessionId, userId: user.id });
      } catch (err) {
        console.warn('[VideoCallPage] Erro ao registrar leaveSession, prosseguindo com navegação:', err);
      }
    }
    navigate(isDoctor ? '/medico/dashboard' : '/dashboard');
  };

  const handleEnd = async () => {
    const targetSessionId = session?.id || sessionId;
    if (user?.id && targetSessionId) {
      try {
        await endSession.mutateAsync({ sessionId: targetSessionId, endedBy: user.id, reason: 'COMPLETED' });
      } catch (err) {
        console.warn('[VideoCallPage] Erro ao registrar endSession, prosseguindo com navegação:', err);
      }
    }
    navigate(isDoctor ? `/medico/telemedicine/summary/${targetSessionId || sessionId}` : `/telemedicine/call-ended/${targetSessionId || sessionId}`);
  };

  const handleSendMessage = useCallback(async (content: string, attachment?: { fileUrl: string; fileName: string; fileSize?: number; mimeType?: string }) => {
    if (!user?.id || !sessionId) return;
    const participantId = localParticipantId || user.id;

    const messageId = crypto.randomUUID();
    const msg: ChatMessage = {
      id: messageId,
      participantId,
      content,
      timestamp: new Date().toISOString(),
      attachment,
    };
    setChatMessages(prev => (prev.some(m => m.id === messageId) ? prev : [...prev, msg]));

    try {
      // Serializa anexo no payload para que o outro participante veja o documento
      const wireContent = attachment ? `${content}\n[MED_ATTACHMENT]:${JSON.stringify(attachment)}` : content;
      await telemedicineApi.emitChatMessage({
        sessionId,
        messageId,
        participantId,
        content: wireContent,
      });
    } catch (err) {
      console.warn('[VideoCallPage] Aviso ao emitir chat por WebSocket (mensagem preservada localmente):', err);
    }
  }, [user?.id, sessionId, localParticipantId]);

  const handleSaveSoap = () => {
    setSoapSaved(true);
    setTimeout(() => setSoapSaved(false), 2500);
  };

  const isDoctor = Boolean(
    session && (
      user?.id === session.doctorId ||
      profile?.id === session.doctorId ||
      profile?.userId === session.doctorId ||
      user?.role === 'DOCTOR'
    )
  );

  const {
    data: prescriptions = [],
    isLoading: isPrescriptionsLoading,
    refetch: refetchPrescriptions,
    isFetching: isFetchingPrescriptions,
  } = useQuery<Prescription[]>({
    queryKey: ['telemedicine', 'prescriptions', session?.appointmentId, session?.patientId],
    queryFn: async () => {
      if (!session?.patientId) return [];
      try {
        const res = await medicalRecordsApi.getPrescriptions(session.patientId, 1, 20);
        const list = res?.data || [];
        if (session.appointmentId) {
          const appointmentMatches = list.filter((p: any) => p.appointmentId === session.appointmentId);
          if (appointmentMatches.length > 0) return appointmentMatches;
        }
        return list;
      } catch (err) {
        console.warn('[VideoCallPage] Error fetching prescriptions:', err);
        return [];
      }
    },
    enabled: Boolean(session?.patientId),
    refetchInterval: 5000,
  });

  const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] = useState(false);
  const [prescriptionModalTab, setPrescriptionModalTab] = useState<'prescription' | 'certificate' | 'exam'>('prescription');

  // Carrega os dados do paciente para pré-preenchimento no modal da MeMed
  const { data: consultationPatient = null } = useQuery({
    queryKey: ['telemedicine', 'patient', session?.patientId],
    queryFn: async () => {
      if (!session?.patientId) return null;
      try {
        const patients = await doctorsApi.getMyPatients(1, 100, '').then((r) => r.patients);
        const match = patients.find((p) => p.id === session.patientId);
        if (match) return match;
      } catch (err) {
        console.warn('[VideoCallPage] Erro ao buscar paciente em getMyPatients:', err);
      }
      return {
        id: session.patientId,
        name: 'Paciente em Atendimento',
      };
    },
    enabled: Boolean(session?.patientId),
  });

  const handleOpenMemedPrescription = () => {
    setPrescriptionModalTab('prescription');
    setIsPrescriptionModalOpen(true);
  };

  const handleOpenMemedCertificate = () => {
    setPrescriptionModalTab('certificate');
    setIsPrescriptionModalOpen(true);
  };

  const handleOpenMemedExams = () => {
    setPrescriptionModalTab('exam');
    setIsPrescriptionModalOpen(true);
  };

  const handleDocumentIssued = (doc: {
    type: 'prescription' | 'certificate' | 'exam';
    id: string;
    link?: string;
    unlockCode?: string;
    title: string;
  }) => {
    refetchPrescriptions();
    const pinInfo = doc.unlockCode ? ` • Código PIN: ${doc.unlockCode}` : '';
    const noticeContent = `📄 **${doc.title}** emitida e assinada via MeMed (ICP-Brasil).${pinInfo}`;
    const attachment = doc.link
      ? {
          fileUrl: doc.link,
          fileName: `${doc.title}.pdf`,
          mimeType: 'application/pdf',
        }
      : undefined;

    handleSendMessage(noticeContent, attachment);
    setActiveTab('chat');
    setIsPrescriptionModalOpen(false);
    if (typeof document !== 'undefined') {
      document.body.classList.remove('memed-in-telemedicine');
    }
  };

  return (
    <div className="h-[100dvh] max-h-[100dvh] overflow-hidden bg-neutral-950 text-foreground flex flex-col relative select-none">
      <header className="h-12 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-b border-neutral-200 dark:border-neutral-800 px-3 sm:px-6 flex items-center justify-between shrink-0 z-20">
        <div className="flex items-center gap-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
          <div>
            <h1 className="text-xs sm:text-sm font-bold text-gray-900 dark:text-gray-100 leading-tight">Teleconsulta</h1>
            <p className="text-[10px] text-gray-500 truncate max-w-[130px] sm:max-w-none">
              #{sessionId?.substring(0, 8)} · {isDoctor ? 'Médico' : 'Paciente'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isRecording && (
            <span className="text-[10px] font-bold text-red-600 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 px-2 py-0.5 rounded-full flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping" /> REC
            </span>
          )}

          {/* Botão para abrir/fechar Prontuário & Chat no Mobile */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMobileDrawerOpen((prev) => !prev)}
            className="lg:hidden h-8 px-2.5 text-xs font-semibold gap-1.5 text-primary"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Prontuário & Chat</span>
          </Button>

          {/* Botões Sair e Encerrar no desktop */}
          <div className="hidden sm:flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleLeave}>Sair</Button>
            {isDoctor && (
              <Button size="sm" onClick={handleEnd} className="bg-red-600 hover:bg-red-700 text-white">
                Encerrar
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 w-full flex flex-col lg:grid lg:grid-cols-4 gap-0 lg:gap-4 p-0 lg:p-4 min-h-0 overflow-hidden relative">
        {/* Video Stage Column */}
        <div className="lg:col-span-3 flex-1 flex flex-col h-full min-h-0 relative overflow-hidden bg-neutral-950 rounded-none lg:rounded-2xl">
          <div className="flex-1 w-full h-full min-h-0 relative overflow-hidden flex items-center justify-center">
            {authenticatedRoomUrl ? (
              <VideoStage
                participants={participants}
                localUserId={user?.id}
                cameraOn={cameraOn}
                microphoneOn={microphoneOn}
                screenShareOn={screenShareOn}
                connectionState={connectionState}
                roomUrl={authenticatedRoomUrl}
                isRecording={isRecording}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-white p-6 space-y-4">
                <div className="w-12 h-12 rounded-full border-4 border-emerald-500/20 border-t-emerald-500 animate-spin flex items-center justify-center" />
                <div className="text-center">
                  <p className="text-sm sm:text-base font-bold text-white">Preparando sala de teleconsulta...</p>
                  <p className="text-xs text-neutral-400 mt-1">Conectando canal seguro e autenticado da Daily.co</p>
                </div>
              </div>
            )}
          </div>

          {/* Floating Bottom MediaControls Bar */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 pointer-events-auto">
            <MediaControls
              cameraOn={cameraOn}
              microphoneOn={microphoneOn}
              screenShareOn={screenShareOn}
              isRecording={isRecording}
              onCameraToggle={handleCameraToggle}
              onMicrophoneToggle={handleMicrophoneToggle}
              onScreenShareToggle={handleScreenShareToggle}
              onRecordingToggle={isDoctor ? handleRecordingToggle : undefined}
              onLeaveOrEnd={isDoctor ? handleEnd : handleLeave}
              onOpenChat={() => setIsMobileDrawerOpen((prev) => !prev)}
              unreadChatCount={chatMessages.length}
              isDoctor={isDoctor}
              isDailyRoom={Boolean(authenticatedRoomUrl?.startsWith('http'))}
              disabled={isStarting || isStopping}
            />
          </div>
        </div>

        {/* Mobile Backdrop when Drawer is open */}
        {isMobileDrawerOpen && (
          <div
            onClick={() => setIsMobileDrawerOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-xs z-35 lg:hidden animate-in fade-in duration-200"
          />
        )}

        {/* Right Drawer / Clinical Workspace */}
        <aside
          className={`
            fixed inset-x-0 bottom-0 z-40 bg-white dark:bg-neutral-900 rounded-t-3xl lg:rounded-2xl shadow-2xl border-t lg:border border-neutral-200 dark:border-neutral-800 flex flex-col overflow-hidden transition-transform duration-300 ease-in-out
            h-[60dvh] sm:h-[65dvh] max-h-[520px]
            lg:static lg:h-full lg:max-h-none lg:translate-y-0 lg:col-span-1 lg:z-auto
            ${isMobileDrawerOpen ? 'translate-y-0' : 'translate-y-full lg:translate-y-0'}
          `}
        >
          {/* Mobile Drawer Handle and Header */}
          <div className="lg:hidden flex flex-col border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
            <div className="w-full flex justify-center pt-2 pb-1 cursor-grab" onClick={() => setIsMobileDrawerOpen(false)}>
              <div className="w-10 h-1 bg-neutral-300 dark:bg-neutral-600 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-4 pb-2">
              <span className="font-bold text-xs uppercase tracking-wider text-neutral-600 dark:text-neutral-400">
                {isDoctor ? 'Prontuário & Chat' : 'Chat & Documentos'}
              </span>
              <button
                type="button"
                onClick={() => setIsMobileDrawerOpen(false)}
                className="p-1 rounded-full text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
                title="Fechar painel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {/* Tabs for doctors */}
          {isDoctor ? (
            <div className="grid grid-cols-4 border-b border-neutral-200 bg-neutral-50 text-xs font-semibold">
              <button
                onClick={() => setActiveTab('chat')}
                className={`py-2.5 flex flex-col items-center gap-1 transition-all ${
                  activeTab === 'chat' ? 'bg-white text-primary border-b-2 border-primary font-bold' : 'text-neutral-500 hover:bg-neutral-100'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>Chat</span>
              </button>
              <button
                onClick={() => setActiveTab('triage')}
                className={`py-2.5 flex flex-col items-center gap-1 transition-all ${
                  activeTab === 'triage' ? 'bg-white text-primary border-b-2 border-primary font-bold' : 'text-neutral-500 hover:bg-neutral-100'
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>Triagem</span>
              </button>
              <button
                onClick={() => setActiveTab('soap')}
                className={`py-2.5 flex flex-col items-center gap-1 transition-all ${
                  activeTab === 'soap' ? 'bg-white text-primary border-b-2 border-primary font-bold' : 'text-neutral-500 hover:bg-neutral-100'
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                <span>SOAP</span>
              </button>
              <button
                onClick={() => setActiveTab('prescriptions')}
                className={`py-2.5 flex flex-col items-center gap-1 transition-all ${
                  activeTab === 'prescriptions' ? 'bg-white text-primary border-b-2 border-primary font-bold' : 'text-neutral-500 hover:bg-neutral-100'
                }`}
              >
                <Pill className="w-3.5 h-3.5" />
                <span>Receita</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 border-b border-neutral-200 bg-neutral-50 text-xs font-semibold">
              <button
                onClick={() => setActiveTab('chat')}
                className={`py-2.5 flex items-center justify-center gap-1.5 transition-all ${
                  activeTab === 'chat' ? 'bg-white text-primary border-b-2 border-primary font-bold' : 'text-neutral-500 hover:bg-neutral-100'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>Chat & Exames</span>
              </button>
              <button
                onClick={() => setActiveTab('prescriptions')}
                className={`py-2.5 flex items-center justify-center gap-1.5 transition-all ${
                  activeTab === 'prescriptions' ? 'bg-white text-primary border-b-2 border-primary font-bold' : 'text-neutral-500 hover:bg-neutral-100'
                }`}
              >
                <Pill className="w-3.5 h-3.5" />
                <span>Receitas {prescriptions.length > 0 ? `(${prescriptions.length})` : ''}</span>
              </button>
            </div>
          )}

          {/* TAB 1: Chat & Participants */}
          {activeTab === 'chat' && (
            <div className="flex-1 flex flex-col p-2.5 sm:p-3 space-y-2 overflow-hidden">
              <div className="text-xs text-neutral-500 font-medium flex items-center justify-between pb-1.5 border-b">
                <span>Participantes ({participants.length})</span>
                <span className="flex items-center gap-1 text-emerald-600 font-semibold text-[11px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Ao vivo
                </span>
              </div>
              <div className="hidden lg:block max-h-24 overflow-y-auto">
                <ParticipantsList participants={participants} localUserId={user?.id} />
              </div>
              <div className="flex-1 overflow-y-auto min-h-0">
                <SessionChat
                  messages={chatMessages}
                  localParticipantId={localParticipantId || user?.id || ''}
                  onSend={handleSendMessage}
                  disabled={connectionState !== 'CONNECTED'}
                  userId={user?.id}
                />
              </div>
            </div>
          )}

          {/* TAB 2: AI Triage Summary */}
          {isDoctor && activeTab === 'triage' && (
            <div className="flex-1 p-3.5 sm:p-4 space-y-3 overflow-y-auto text-xs">
              <div className="flex items-center justify-between font-bold text-primary text-sm pb-1.5 border-b">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span>Briefing Clínico de IA</span>
                </div>
                {parsedTriage?.riskClassification && (
                  <span className="text-[10px] uppercase font-extrabold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20">
                    {parsedTriage.riskClassification}
                  </span>
                )}
              </div>

              <div>
                <span className="text-neutral-400 font-medium uppercase text-[10px] block mb-0.5">Queixa Principal:</span>
                <p className="font-semibold text-neutral-900 dark:text-neutral-100 bg-neutral-50 dark:bg-neutral-800/60 p-2.5 rounded-xl border border-neutral-200 dark:border-neutral-700">
                  {parsedTriage?.chiefComplaint || parsedTriage?.summary || 'Consulta expressa solicitada pelo paciente.'}
                </p>
              </div>

              <div>
                <span className="text-neutral-400 font-medium uppercase text-[10px] block mb-0.5">Resumo dos Sintomas:</span>
                <p className="text-neutral-700 dark:text-neutral-300 bg-neutral-50 dark:bg-neutral-800/60 p-2.5 rounded-xl border border-neutral-200 dark:border-neutral-700 leading-relaxed">
                  {parsedTriage?.summary || parsedTriage?.symptomDetail || 'Paciente aguardando atendimento clínico imediato.'}
                </p>
              </div>

              {((parsedTriage?.symptomIntensity !== undefined && parsedTriage?.symptomIntensity !== null) || parsedTriage?.symptomDuration) && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {parsedTriage?.symptomIntensity !== undefined && parsedTriage?.symptomIntensity !== null && (
                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 p-2 rounded-xl text-blue-900 dark:text-blue-300">
                      <span className="text-[10px] uppercase font-bold text-blue-600 dark:text-blue-400 block">Intensidade da Dor</span>
                      <span className="font-extrabold text-sm">{parsedTriage.symptomIntensity}/10</span>
                    </div>
                  )}
                  {parsedTriage?.symptomDuration && (
                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 p-2 rounded-xl text-blue-900 dark:text-blue-300">
                      <span className="text-[10px] uppercase font-bold text-blue-600 dark:text-blue-400 block">Duração</span>
                      <span className="font-extrabold text-sm">{parsedTriage.symptomDuration}</span>
                    </div>
                  )}
                </div>
              )}

              {parsedTriage?.recommendedSpecialty && (
                <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-2.5 rounded-xl text-emerald-900 dark:text-emerald-300 flex items-center justify-between">
                  <span className="font-semibold text-xs">Especialidade Sugerida:</span>
                  <span className="font-extrabold text-xs">{parsedTriage.recommendedSpecialty}</span>
                </div>
              )}

              {Array.isArray(parsedTriage?.redFlags) && parsedTriage.redFlags.length > 0 && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 p-2.5 rounded-xl text-red-800 dark:text-red-300 space-y-1.5">
                  <span className="font-bold text-[10px] uppercase text-red-600 dark:text-red-400 block">Sinais de Alerta Identificados:</span>
                  <div className="flex flex-wrap gap-1">
                    {parsedTriage.redFlags.map((rf: string, idx: number) => (
                      <span key={idx} className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300">
                        {rf}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {parsedTriage?.medicalHistory && (
                <div>
                  <span className="text-neutral-400 font-medium uppercase text-[10px] block mb-0.5">Histórico Relevante:</span>
                  <p className="text-neutral-700 dark:text-neutral-300 bg-neutral-50 dark:bg-neutral-800/60 p-2.5 rounded-xl border border-neutral-200 dark:border-neutral-700">
                    {parsedTriage.medicalHistory}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: SOAP Notes */}
          {isDoctor && activeTab === 'soap' && (
            <div className="flex-1 p-3 space-y-2 overflow-y-auto text-xs flex flex-col">
              <div className="flex items-center justify-between pb-1 border-b">
                <span className="font-bold text-neutral-900 text-sm">Registro SOAP</span>
                {soapSaved && (
                  <span className="text-emerald-600 flex items-center gap-1 font-semibold text-[11px]">
                    <Check className="w-3.5 h-3.5" /> Salvo
                  </span>
                )}
              </div>
              <div>
                <label className="font-bold text-neutral-600 block mb-0.5">S (Subjetivo):</label>
                <textarea
                  value={soapSubjective}
                  onChange={(e) => setSoapSubjective(e.target.value)}
                  placeholder="Relato do paciente, queixas..."
                  className="w-full h-14 p-2 rounded-lg border text-xs focus:ring-1 focus:ring-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="font-bold text-neutral-600 block mb-0.5">O (Objetivo):</label>
                <textarea
                  value={soapObjective}
                  onChange={(e) => setSoapObjective(e.target.value)}
                  placeholder="Exame visual, sinais vitais referidos..."
                  className="w-full h-14 p-2 rounded-lg border text-xs focus:ring-1 focus:ring-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="font-bold text-neutral-600 block mb-0.5">A (Avaliação / CID):</label>
                <textarea
                  value={soapAssessment}
                  onChange={(e) => setSoapAssessment(e.target.value)}
                  placeholder="Hipótese diagnóstica, CID-10..."
                  className="w-full h-14 p-2 rounded-lg border text-xs focus:ring-1 focus:ring-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="font-bold text-neutral-600 block mb-0.5">P (Plano / Conduta):</label>
                <textarea
                  value={soapPlan}
                  onChange={(e) => setSoapPlan(e.target.value)}
                  placeholder="Prescrição, atestado, orientações..."
                  className="w-full h-14 p-2 rounded-lg border text-xs focus:ring-1 focus:ring-primary focus:outline-none"
                />
              </div>
              <Button
                onClick={handleSaveSoap}
                size="sm"
                className="w-full mt-2 gap-1.5 text-xs font-semibold"
              >
                <Save className="w-3.5 h-3.5" />
                Salvar Evolução
              </Button>
            </div>
          )}

          {/* TAB 4: Prescriptions (Memed Shortcut & Patient View) */}
          {activeTab === 'prescriptions' && (
            <div className="flex-1 p-4 space-y-3 overflow-y-auto text-xs">
              <div className="flex items-center justify-between gap-1.5 font-bold text-primary text-sm pb-1 border-b">
                <div className="flex items-center gap-1.5">
                  <Pill className="w-4 h-4 text-emerald-600" />
                  <span>Prescrição Digital MeMed</span>
                </div>
                {prescriptions.length > 0 && (
                  <span className="text-[10px] font-extrabold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 rounded-full">
                    {prescriptions.length} {prescriptions.length === 1 ? 'receita' : 'receitas'}
                  </span>
                )}
              </div>

              <p className="text-neutral-500 leading-relaxed">
                {isDoctor
                  ? 'Emita receitas médicas, pedidos de exames e atestados com assinatura digital ICP-Brasil e envio automático por SMS para o paciente.'
                  : 'Receitas, pedidos de exames e atestados emitidos durante esta teleconsulta com assinatura oficial ICP-Brasil.'}
              </p>

              {isDoctor && (
                <div className="space-y-2 pt-1">
                  <Button
                    onClick={handleOpenMemedPrescription}
                    className="w-full text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs gap-1.5"
                  >
                    <Pill className="w-3.5 h-3.5" />
                    <span>Abrir Emissor MeMed Sinapse (Receita Digital)</span>
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={handleOpenMemedCertificate}
                      variant="outline"
                      className="w-full text-xs"
                    >
                      Emitir Atestado
                    </Button>
                    <Button
                      onClick={handleOpenMemedExams}
                      variant="outline"
                      className="w-full text-xs"
                    >
                      Solicitar Exames
                    </Button>
                  </div>
                </div>
              )}

              {/* Lista ou status das receitas emitidas */}
              <div className="pt-2 border-t border-neutral-200 dark:border-neutral-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[11px] uppercase tracking-wider text-muted-foreground">
                    Receitas da Consulta
                  </span>
                  <button
                    type="button"
                    onClick={() => refetchPrescriptions()}
                    disabled={isFetchingPrescriptions}
                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                    title="Atualizar receitas"
                  >
                    <RefreshCw className={`w-3 h-3 ${isFetchingPrescriptions ? 'animate-spin' : ''}`} />
                    <span>Atualizar</span>
                  </button>
                </div>

                {isPrescriptionsLoading ? (
                  <div className="p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg text-center text-muted-foreground">
                    Carregando receitas...
                  </div>
                ) : prescriptions.length > 0 ? (
                  <div className="space-y-2.5">
                    {prescriptions.map((prescription) => (
                      <ConsultationPrescriptionCard
                        key={prescription.id}
                        prescription={prescription}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="p-3 bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-900/40 rounded-xl space-y-1.5">
                    <div className="flex items-center gap-1.5 font-bold text-emerald-800 dark:text-emerald-300">
                      <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>Certificação ICP-Brasil Ativa</span>
                    </div>
                    <p className="text-[11px] text-emerald-900/80 dark:text-emerald-300/80 leading-relaxed">
                      Nenhuma receita emitida nesta consulta ainda. Ao clicar em <strong>Abrir Emissor MeMed Sinapse</strong>, o link oficial e o <strong>PIN de desbloqueio</strong> aparecerão automaticamente aqui após a assinatura.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      {isDoctor && (
        <TelemedicinePrescriptionModal
          isOpen={isPrescriptionModalOpen}
          onClose={() => {
            setIsPrescriptionModalOpen(false);
            if (typeof document !== 'undefined') {
              document.body.classList.remove('memed-in-telemedicine');
            }
          }}
          patient={consultationPatient as any}
          doctorId={session?.doctorId || profile?.id || user?.id || ''}
          appointmentId={session?.appointmentId}
          initialTab={prescriptionModalTab}
          onDocumentIssued={handleDocumentIssued}
        />
      )}
    </div>
  );
}

function ConsultationPrescriptionCard({
  prescription,
}: {
  prescription: Prescription;
}) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPin, setCopiedPin] = useState(false);
  const [fetchedData, setFetchedData] = useState<{ link?: string; unlockCode?: string } | null>(null);
  const [isFetchingLink, setIsFetchingLink] = useState(false);

  const initialLink =
    prescription.digitalPrescription?.link ||
    prescription.content?.digitalPrescription?.link ||
    prescription.digitalPrescriptionLink ||
    prescription.memedPdfUrl ||
    prescription.pdfUrl ||
    null;

  const initialUnlockCode =
    prescription.digitalPrescription?.unlockCode ||
    prescription.content?.digitalPrescription?.unlockCode ||
    prescription.unlockCode ||
    null;

  const currentLink = fetchedData?.link || initialLink;
  const currentUnlockCode = fetchedData?.unlockCode || initialUnlockCode;
  const targetPdf = prescription.memedPdfUrl || prescription.pdfUrl;

  const handleFetchLink = async () => {
    if (!prescription.id) return;
    setIsFetchingLink(true);
    try {
      const res = await medicalRecordsApi.getDigitalPrescriptionLink(prescription.id);
      if (res?.link || res?.unlockCode) {
        setFetchedData({ link: res.link, unlockCode: res.unlockCode });
      }
    } catch {
      // Ignora erro se não disponível ainda
    } finally {
      setIsFetchingLink(false);
    }
  };

  const handleCopyLink = () => {
    if (!currentLink) return;
    navigator.clipboard?.writeText(currentLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyPin = () => {
    if (!currentUnlockCode) return;
    navigator.clipboard?.writeText(currentUnlockCode);
    setCopiedPin(true);
    setTimeout(() => setCopiedPin(false), 2000);
  };

  return (
    <div className="p-3 bg-white dark:bg-card border border-emerald-200 dark:border-emerald-900/60 rounded-xl space-y-2 shadow-2xs">
      <div className="flex items-center justify-between gap-1.5 flex-wrap">
        <span className="font-mono font-bold text-xs text-foreground">
          #{prescription.id.slice(0, 8)}
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950 px-2 py-0.5 rounded-full border border-emerald-300/60 dark:border-emerald-800">
          <ShieldCheck className="w-3 h-3 text-emerald-600" />
          ICP-Brasil
        </span>
      </div>

      {/* Medicamentos */}
      {prescription.medications && prescription.medications.length > 0 && (
        <div className="text-[11px] text-neutral-700 dark:text-neutral-300 space-y-0.5 pt-0.5">
          {prescription.medications.map((m, idx) => (
            <p key={idx} className="font-medium truncate">
              • {m.nome || m.name} {m.concentration ? `(${m.concentration})` : ''}
            </p>
          ))}
        </div>
      )}

      {/* Link e PIN de Desbloqueio */}
      {currentUnlockCode || currentLink ? (
        <div className="space-y-1.5 pt-1 border-t border-neutral-100 dark:border-neutral-800">
          {currentUnlockCode && (
            <div className="flex items-center justify-between bg-neutral-50 dark:bg-neutral-900 p-1.5 rounded-lg border border-neutral-200/80 dark:border-neutral-800">
              <div className="flex items-center gap-1 text-[11px]">
                <Lock className="w-3 h-3 text-amber-600" />
                <span className="text-muted-foreground">PIN:</span>
                <span className="font-mono font-extrabold text-foreground text-xs">{currentUnlockCode}</span>
              </div>
              <button
                type="button"
                onClick={handleCopyPin}
                className="inline-flex items-center gap-1 text-[10px] font-bold text-neutral-600 dark:text-neutral-300 hover:text-foreground cursor-pointer px-1.5 py-0.5 rounded bg-white dark:bg-card border border-neutral-200 dark:border-neutral-700 shadow-2xs"
              >
                {copiedPin ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                <span>{copiedPin ? 'Copiado' : 'Copiar PIN'}</span>
              </button>
            </div>
          )}

          {currentLink && (
            <div className="flex items-center gap-1.5 pt-0.5">
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex-1 inline-flex items-center justify-center gap-1 py-1 px-2 text-[10px] font-semibold rounded bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 text-neutral-800 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700 cursor-pointer shadow-2xs"
              >
                {copiedLink ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                <span>{copiedLink ? 'Link Copiado' : 'Copiar Link'}</span>
              </button>
              <button
                type="button"
                onClick={() => window.open(currentLink, '_blank', 'noopener,noreferrer')}
                className="flex-1 inline-flex items-center justify-center gap-1 py-1 px-2 text-[10px] font-bold rounded bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer shadow-2xs"
              >
                <ExternalLink className="w-3 h-3" />
                <span>Abrir Receita</span>
              </button>
            </div>
          )}
        </div>
      ) : prescription.memedPrescriptionId ? (
        <div className="pt-1">
          <button
            type="button"
            disabled={isFetchingLink}
            onClick={handleFetchLink}
            className="w-full inline-flex items-center justify-center gap-1 py-1 text-[10px] font-semibold rounded bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 cursor-pointer"
          >
            <Key className="w-3 h-3" />
            <span>{isFetchingLink ? 'Consultando...' : 'Obter PIN e Link da MeMed'}</span>
          </button>
        </div>
      ) : null}

      {targetPdf && (
        <button
          type="button"
          onClick={() => window.open(targetPdf, '_blank', 'noopener,noreferrer')}
          className="w-full inline-flex items-center justify-center gap-1 text-[10px] text-muted-foreground hover:text-foreground pt-1 text-center cursor-pointer"
        >
          <ExternalLink className="w-3 h-3" />
          <span>Visualizar PDF anexado</span>
        </button>
      )}
    </div>
  );
}

